import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_LIGHT, LOGO_DARK } from "@/lib/constants";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "./LanguageToggle";

const links = [
  { href: "#story", key: "nav.story", id: "story" },
  { href: "#menu", key: "nav.menu", id: "menu" },
  { href: "#brunch", key: "nav.brunch", id: "brunch" },
  { href: "#signature", key: "nav.signature", id: "signature" },
  { href: "#order", key: "nav.order", id: "order" },
  { href: "#gallery", key: "nav.gallery", id: "gallery" },
  { href: "#visit", key: "nav.visit", id: "visit" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 md:top-9 ${
        scrolled
          ? "bg-[#FDFBF7]/92 backdrop-blur-xl border-b border-[#E4DECD]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        <div className="hidden lg:flex items-center gap-7 flex-1">
          {links.slice(0, 4).map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className={`font-mono-accent text-[13px] brass-link ${
                scrolled ? "text-[#1A1713]" : "text-[#FDFBF7]"
              }`}
            >
              {t(l.key)}
            </a>
          ))}
        </div>

        <a
          href="#top"
          data-testid="nav-logo"
          className="flex-shrink-0 flex items-center"
        >
          <img
            src={scrolled ? LOGO_LIGHT : LOGO_DARK}
            alt="Ladiosa's Homestyle Kitchen"
            className="h-20 md:h-24 w-auto object-contain transition-opacity duration-500"
          />
        </a>

        <div className="hidden lg:flex items-center gap-7 flex-1 justify-end">
          {links.slice(4).map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className={`font-mono-accent text-[13px] brass-link ${
                scrolled ? "text-[#1A1713]" : "text-[#FDFBF7]"
              }`}
            >
              {t(l.key)}
            </a>
          ))}
          <LanguageToggle tone={scrolled ? "light" : "dark"} />
          <a
            href="#reserve"
            data-testid="nav-reserve-btn"
            className={`font-mono-accent text-[13px] px-5 py-3 transition-colors duration-300 ${
              scrolled
                ? "bg-[#1A1713] text-[#FDFBF7] hover:bg-[#7A2021]"
                : "bg-[#FDFBF7] text-[#1A1713] hover:bg-[#C98A4B]"
            }`}
          >
            {t("nav.reserve")}
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className={`lg:hidden ${scrolled ? "text-[#1A1713]" : "text-[#FDFBF7]"}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden bg-[#FDFBF7] border-t border-[#E4DECD] px-6 py-8 flex flex-col gap-5"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.id}`}
              className="font-serif-display text-3xl text-[#1A1713]"
            >
              {t(l.key)}
            </a>
          ))}
          <div className="pt-3">
            <LanguageToggle tone="light" />
          </div>
          <a
            href="#reserve"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-reserve"
            className="font-mono-accent text-xs bg-[#1A1713] text-[#FDFBF7] px-5 py-4 text-center mt-2"
          >
            {t("nav.reserve")}
          </a>
        </div>
      )}
    </header>
  );
}
